import { useState, useCallback } from 'react';
import { Wand2, Loader2, Copy, Check, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const AI_TARGETS = [
  { id: 'chatgpt', label: 'ChatGPT' }, { id: 'claude', label: 'Claude' },
  { id: 'gemini', label: 'Gemini' }, { id: 'perplexity', label: 'Perplexity' },
  { id: 'copilot', label: 'Copilot' }, { id: 'cursor', label: 'Cursor' },
  { id: 'emergent', label: 'Emergent' },
];

const SAMPLES = [
  { label: 'Business plan', text: 'write me a business plan', ai: 'chatgpt' },
  { label: 'Debug React', text: 'fix my useEffect infinite loop', ai: 'cursor' },
  { label: 'Research paper', text: 'summarize recent AI safety papers', ai: 'perplexity' },
  { label: 'Image prompt', text: 'create a hero image for a fintech app', ai: 'chatgpt' },
];

export default function PICraftPage() {
  const [text, setText] = useState('');
  const [targetAi, setTargetAi] = useState('chatgpt');
  const [devMode, setDevMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleCraft = useCallback(async () => {
    if (text.trim().length < 3 || loading) return;
    setLoading(true); setResult(null);
    try {
      const res = await fetch(`${API}/api/ai/prompt`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: text, target_ai: targetAi, task_type: 'general', dev_mode: devMode }),
      });
      if (res.ok) setResult(await res.json());
      else throw new Error('Craft failed');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [text, targetAi, devMode, loading]);

  const handleCopy = () => {
    if (result?.enhanced) { navigator.clipboard.writeText(result.enhanced); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  const handleSave = async () => {
    if (!result?.enhanced || saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/ai/my-casts`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cast: result.enhanced, title: text.slice(0, 60), target_ai: targetAi, source: 'pi_craft' }),
      });
      if (res.ok) toast.success('Cast saved to library!');
    } catch (err) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="picraft-page">
      <div>
        <h1 className="font-space text-2xl md:text-3xl text-white mb-2">PI Craft</h1>
        <p className="text-white/40 text-sm">Transform rough text into precision casts for any AI platform</p>
      </div>

      {/* How to use */}
      <div className="bg-[#0E1830] border border-white/5 rounded-xl p-4">
        <details>
          <summary className="text-white/50 text-xs font-bold uppercase tracking-wider cursor-pointer hover:text-white/70">How to use PI Craft</summary>
          <div className="mt-3 text-white/35 text-xs space-y-1 leading-relaxed">
            <p>1. Type your rough idea or request in plain English</p>
            <p>2. Select the target AI platform (ChatGPT, Claude, Gemini, etc.)</p>
            <p>3. Toggle &ldquo;Dev Mode&rdquo; for coding-specific casts</p>
            <p>4. Click &ldquo;Craft&rdquo; to transform it into a structured precision cast</p>
            <p>5. Copy the result and paste directly into your AI platform</p>
          </div>
        </details>
      </div>

      {/* Sample prompts */}
      <div className="flex flex-wrap gap-2">
        <span className="text-white/20 text-xs self-center">Try:</span>
        {SAMPLES.map(s => (
          <button key={s.label} onClick={() => { setText(s.text); setTargetAi(s.ai); setResult(null); }}
            className="px-3 py-1 text-xs rounded-full border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all">{s.label}</button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6 space-y-4">
          <textarea value={text} onChange={e => { setText(e.target.value); setResult(null); }}
            placeholder="Type your rough idea... e.g. 'write me a sales email'"
            className="w-full h-28 bg-white/[0.04] border border-white/[0.08] rounded-xl p-4 text-white/90 text-sm resize-none focus:border-[#7C3AED]/40 focus:outline-none placeholder:text-white/20"
            data-testid="craft-input" />

          <div className="space-y-2">
            <span className="text-[10px] uppercase tracking-widest text-white/30 font-bold">Target AI</span>
            <div className="flex flex-wrap gap-1.5">
              {AI_TARGETS.map(ai => (
                <button key={ai.id} onClick={() => setTargetAi(ai.id)}
                  className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-all ${targetAi === ai.id ? 'bg-[#7C3AED] text-white border-[#7C3AED]' : 'bg-white/[0.03] text-white/40 border-white/[0.08] hover:text-white/60'}`}>{ai.label}</button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={devMode} onChange={e => setDevMode(e.target.checked)}
              className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#7C3AED] focus:ring-[#7C3AED]" />
            <span className="text-white/40 text-xs">Dev Mode (coding-specific casts)</span>
          </label>

          <Button onClick={handleCraft} disabled={loading || text.trim().length < 3}
            className="w-full shimmer-btn text-white rounded-full font-space font-bold border-0" data-testid="craft-btn">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Crafting...</> : <><Wand2 className="w-4 h-4 mr-2" /> Craft Precision Cast</>}
          </Button>
        </div>

        {/* Result */}
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
          {result ? (
            <div className="space-y-4" data-testid="craft-result">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-[#06B6D4] uppercase tracking-wider">Precision Cast</span>
                  <p className="text-white/30 text-[10px] mt-0.5">for {result.target_ai} &middot; {result.token_count || '?'} tokens</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={handleCopy} className="text-xs text-[#7C3AED] hover:underline flex items-center gap-1" data-testid="craft-copy">
                    {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                  <button onClick={handleSave} disabled={saving} className="text-xs text-emerald-400 hover:underline flex items-center gap-1" data-testid="craft-save">
                    <Save className="w-3 h-3" /> {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 max-h-[350px] overflow-y-auto">
                <pre className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap font-mono m-0">{result.enhanced}</pre>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <Wand2 className="w-10 h-10 text-white/10 mb-3" />
              <p className="text-white/30 text-sm">Type your rough idea to craft a precision cast</p>
              <p className="text-white/15 text-xs mt-1">Auto-detects AI platform best practices</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
